import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JobLogLineStore from "~/stores/JobLogLineStore";
import JobLogGroupStore from "~/stores/JobLogGroupStore";
import JobLogStore from "~/stores/JobLogStore";
import { parseHash } from "~/stores/BuildPageStore";
import { Job } from "~/components/build/Show/lib/types";

type LogLineSelectionContextValue = {
  selectLine: (jobId: Job["id"], lineNumber: number) => void;
  selectRange: (jobId: Job["id"], startLine: number, endLine: number) => void;
  clearSelection: (jobId: Job["id"]) => void;
};

const LogLineSelectionContext = createContext<LogLineSelectionContextValue | null>(null);

export function useLogLineSelection(): LogLineSelectionContextValue {
  const context = useContext(LogLineSelectionContext);
  if (!context) {
    throw new Error("useLogLineSelection must be used within a LogLineSelectionProvider");
  }
  return context;
}

export function LogLineSelectionProvider({ children }: { children: React.ReactNode }) {
  const { hash, search } = useLocation();
  const navigate = useNavigate();

  // Track whether the current hash change was triggered by user interaction (clicking a line number)
  // vs external navigation (page load, shared link, browser back/forward).
  // When true, we skip auto-expanding groups to preserve the user's current expand/collapse state.
  const isUserInitiatedSelectionRef = useRef(false);

  // Parse the hash to extract job, group, line, and endLine
  const parsedHash = useMemo(
    () =>
      parseHash(hash) || {
        job: undefined,
        group: undefined,
        line: undefined,
        endLine: undefined,
      },
    [hash],
  );
  const { job, group, line, endLine } = parsedHash;

  // Detect line-only URL format: has line but no group (e.g., #jobId/L54)
  const isLineOnlyFormat = group === undefined && line !== undefined;

  // Detect range URL format: has both line and endLine (e.g., #jobId/L10-L15)
  const isRangeFormat = isLineOnlyFormat && endLine !== undefined;

  // For group-line format (#jobId/group-line), lineId comes directly from the hash
  const lineId = group ? hash.slice(1) : null;

  // Handle group-line format URLs - can highlight immediately since we know the group
  useLayoutEffect(() => {
    if (Features.RefactorLogScrollBehaviour && lineId) {
      JobLogLineStore.highlight(lineId);
      JobLogGroupStore.set(`${job}/${group}`, true);
    }
  }, [job, group, lineId]);

  // Clear any highlighted lines when switching jobs or navigating away from a line reference
  const searchParams = new URLSearchParams(search);
  const sid = searchParams.get("sid");
  const jid = searchParams.get("jid");
  useEffect(() => {
    if (!line && !group) {
      JobLogLineStore.clearAll();
    }
  }, [sid, jid, hash, line, group]);

  // Handle line-only format URLs (#jobId/L54) and range URLs (#jobId/L10-L15)
  // Must wait for log to load to find the group(s)
  useEffect(() => {
    if (!Features.RefactorLogScrollBehaviour || !isLineOnlyFormat || !job || !line) {
      return;
    }

    const startLineNumber = parseInt(line, 10);
    const endLineNumber = endLine ? parseInt(endLine, 10) : startLineNumber;

    if (isNaN(startLineNumber) || isNaN(endLineNumber)) {
      return;
    }

    // Capture and reset the flag at the start of the effect to avoid stale values.
    // If we don't reset here and the log isn't loaded yet (expandAndHighlight returns
    // false early), the flag would remain set and could incorrectly prevent auto-expansion
    // for a subsequent URL navigation.
    const wasUserInitiated = isUserInitiatedSelectionRef.current;
    isUserInitiatedSelectionRef.current = false;

    // Check if this exact selection is already highlighted. This handles the case where
    // the component remounts (e.g., dock position change) but the selection hasn't changed.
    // We check this before the effect runs to avoid re-expanding groups on remount.
    const currentRange = JobLogLineStore.getHighlightedRange(job);
    const isAlreadyHighlighted =
      currentRange && currentRange.start === startLineNumber && currentRange.end === endLineNumber;

    // For single line: expand one group, highlight one line
    // For range: expand all groups containing lines in range, highlight the range
    const expandAndHighlight = () => {
      // Collect all unique group numbers that need to be expanded
      const groupsToExpand = new Set<number>();

      for (let lineNum = startLineNumber; lineNum <= endLineNumber; lineNum++) {
        const groupNum = JobLogStore.getGroupNumberForLine(job, lineNum);
        if (groupNum !== undefined) {
          groupsToExpand.add(groupNum);
        }
      }

      // If we couldn't find any groups, the log isn't fully loaded yet
      if (groupsToExpand.size === 0) {
        return false;
      }

      // Only expand groups if this is an external navigation (e.g., shared link, page load).
      // Skip expansion if:
      // - The user clicked a line number directly (to preserve their expand/collapse state)
      // - The line is already highlighted with the same range (e.g., component remount due to
      //   dock position change - the selection hasn't changed so we shouldn't re-expand)
      if (!wasUserInitiated && !isAlreadyHighlighted) {
        for (const groupNum of groupsToExpand) {
          JobLogGroupStore.set(`${job}/${groupNum}`, true);
        }
      }

      // Highlight the line(s)
      if (isRangeFormat) {
        // Range format: highlight the entire range
        const range = JobLogLineStore.getHighlightedRange(job);
        if (range && range.start === startLineNumber && range.end === endLineNumber) {
          return true; // Already highlighted with same range
        }
        JobLogLineStore.highlightRange(job, startLineNumber, endLineNumber);
        // Set anchor to start of range for subsequent shift+click adjustments
        JobLogLineStore.setAnchorLine(job, startLineNumber);
      } else {
        // Single line format: highlight just that line
        // Check if already highlighted as a single line (not as part of a range)
        const range = JobLogLineStore.getHighlightedRange(job);
        const isAlreadySingleLineHighlight =
          range && range.start === startLineNumber && range.end === startLineNumber;

        if (isAlreadySingleLineHighlight) {
          return true; // Already highlighted as single line
        }

        // Clear any existing range and highlight this single line
        JobLogLineStore.highlightRange(job, startLineNumber, startLineNumber);
        // Set anchor for shift+click
        JobLogLineStore.setAnchorLine(job, startLineNumber);
      }

      return true;
    };

    // Try to expand and highlight immediately (if log is already loaded)
    if (expandAndHighlight()) {
      return;
    }

    // Log not loaded yet - listen for changes
    JobLogStore.addChangeListener(job, expandAndHighlight);

    return () => {
      JobLogStore.removeChangeListener(job, expandAndHighlight);
    };
  }, [job, line, endLine, isLineOnlyFormat, isRangeFormat]);

  // Selection functions that update the URL using React Router
  const selectLine = useCallback(
    (jobId: Job["id"], lineNumber: number) => {
      // Mark this as a user-initiated selection so we don't auto-expand groups
      isUserInitiatedSelectionRef.current = true;

      // Check if clicking the same single-line selection to toggle off
      // Only toggle off if it's a single line (start === end), not a multi-line range
      const currentRange = JobLogLineStore.getHighlightedRange(jobId);
      const isSingleLineSelection = currentRange && currentRange.start === currentRange.end;
      const isClickingSelectedLine = isSingleLineSelection && currentRange.start === lineNumber;

      if (isClickingSelectedLine) {
        // Clear highlight and navigate to just the job
        JobLogLineStore.clearAll();
        navigate({ search, hash: jobId }, { replace: true, preventScrollReset: true });
        return;
      }

      // Check if the current URL already points to this single line (not a range)
      // This handles the case where URL hasn't been synced to store yet
      const currentLineNumber = line ? parseInt(line, 10) : undefined;
      const isCurrentUrlSingleLine = job === jobId && currentLineNumber === lineNumber && !endLine;

      if (isCurrentUrlSingleLine) {
        // Clear highlight and navigate to just the job
        JobLogLineStore.clearAll();
        navigate({ search, hash: jobId }, { replace: true, preventScrollReset: true });
        return;
      }

      // Clicking within a multi-line range or on a different line:
      // Select just this single line (replacing any existing selection)
      // Set anchor for subsequent shift+click range selections
      JobLogLineStore.setAnchorLine(jobId, lineNumber);
      navigate({ search, hash: `${jobId}/L${lineNumber}` }, { replace: true });
    },
    [navigate, search, job, line, endLine],
  );

  const selectRange = useCallback(
    (jobId: Job["id"], startLine: number, endLine: number) => {
      // Mark this as a user-initiated selection so we don't auto-expand groups
      isUserInitiatedSelectionRef.current = true;

      // Update URL with range format
      navigate({ search, hash: `${jobId}/L${startLine}-L${endLine}` }, { replace: true });
    },
    [navigate, search],
  );

  const clearSelection = useCallback(
    (jobId: Job["id"]) => {
      JobLogLineStore.clearAll();
      navigate({ search, hash: jobId }, { replace: true, preventScrollReset: true });
    },
    [navigate, search],
  );

  const value = useMemo(
    () => ({
      selectLine,
      selectRange,
      clearSelection,
    }),
    [selectLine, selectRange, clearSelection],
  );

  return (
    <LogLineSelectionContext.Provider value={value}>{children}</LogLineSelectionContext.Provider>
  );
}
